<script lang="ts">
    import type {DepartmentResponse, RecordHolding} from 'src/typings';
    import type {Row} from '@tanstack/svelte-table';
    import type {HierarchicalRecordRow} from '../../lib/types';
    import type {RecordGridDataManager} from 'src/features/record-grid/lib/record-grid-data-manager';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {findFirstOrGetDefault, isNotEmpty} from 'shared/utils/array-utils';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {getInjector} from 'src/core';
    import {onDestroy} from 'svelte';
    import {KpPopover} from 'shared/ui-widgets/popover';
    import {CurrentAuthService} from 'shared/services';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';

    type DepartmentWithOptionalHolding = { department: DepartmentResponse, holding: RecordHolding | null };

    export let recordGridDataManager: RecordGridDataManager;
    export let row: Row<HierarchicalRecordRow>;

    const currentAuthService = getInjector().getByClass(CurrentAuthService);
    let readableDepartments: DepartmentResponse[];
    const currentAuthSubscription = currentAuthService.currentAuth$().subscribe((auth) => readableDepartments = auth.readableDepartments);

    $: currentRecordRow = row.original;
    $: recordHoldings = currentRecordRow.recordHoldings ?? [];
    $: assignableDepartments = currentRecordRow.assignableDepartments ?? [];
    let departmentsWithOptionalHoldings: DepartmentWithOptionalHolding[];
    $: departmentsWithOptionalHoldings = assignableDepartments.map((department) => ({
        department,
        holding: findAssignedHolding(department)
    })).filter(({department}) => readableDepartments.some((readableDepartment) => readableDepartment.id === department.id));
    $: onlySingleHolding = recordHoldings.length === 1;

    onDestroy(() => {
        cleanup(currentAuthSubscription);
    });

    function findAssignedHolding(department: DepartmentResponse): RecordHolding | null {
        return findFirstOrGetDefault(recordHoldings, (holding) => holding.department.id === department.id, null);
    }

    let processing = false;

    async function handleShareWithDepartmentClick(department: DepartmentResponse) {
        processing = true;
        await recordGridDataManager.createRecordHolding(currentRecordRow, department);
        processing = false;
    }

    async function handleUnshareWithDepartmentClick(recordHolding: RecordHolding) {
        processing = true;
        await recordGridDataManager.deleteRecordHolding(currentRecordRow, recordHolding);
        processing = false;
    }
</script>

<div class="kp-grid-holdings-management-cell">
    {#if isNotEmpty(departmentsWithOptionalHoldings)}
        <KpPopover buttonSize="xs" isLoading={processing} additionalPopoverButtonClasses="share-with-btn">
            <IconedContent slot="button" icon="link"/>

            <div slot="popover-content">
                {#each departmentsWithOptionalHoldings as {department, holding} (department.id)}
                    {#if exists(holding)}
                        <button class="holding-btn"
                                disabled={onlySingleHolding}
                                on:click={() => handleUnshareWithDepartmentClick(holding)}>

                            <span class="button-label">{pipe(department, loc())}</span>
                            <span class="icon-container">
                                <UIcon icon="check"/>
                            </span>
                        </button>
                    {:else}
                        <button class="holding-btn" on:click={() => handleShareWithDepartmentClick(department)}>
                            <span class="button-label">{pipe(department, loc())}</span>
                        </button>
                    {/if}
                {/each}
            </div>
        </KpPopover>
    {/if}

    {#each recordHoldings as recordHolding(recordHolding.id)}
        <KpChipTag chipSize="sm" squaredCorners customPadding={5}>
            {pipe(recordHolding.department, loc())}
        </KpChipTag>
    {/each}
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .kp-grid-holdings-management-cell {
        display: flex;
        align-items: center;
        overflow: hidden;
        height: 100%;
        gap: @spacing-xs;

        :global(.share-with-btn) {
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .holding-btn {
            display: flex;
            align-items: center;
            padding: @spacing-xs @spacing-m;
            gap: @spacing-m;
            width: 100%;
            font-weight: normal;
            border-radius: @border-radius-default;
            color: var(--text-default);
            white-space: nowrap;
            outline: none;
            border: none;
            background-color: transparent;
            cursor: pointer;
            text-decoration: none;

            &:disabled {
                opacity: 0.5;
                background-color: @themed-disabled-bg;
                cursor: not-allowed;
            }

            &:not(:disabled):hover {
                background-color: @themed-body-bg-blue-highlighted;
            }

            .button-label {
                flex: 1;
                text-align: start;
                font-size: @font-size-sm;
            }

            .icon-container {
                color: @themed-text-muted;
                font-size: @font-size-small;
            }
        }
    }
</style>